import React, { useEffect, useMemo, useState } from 'react';
import { Download, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from './Toast';
import { drawQrCard } from '../utils/qrCard';
import { applyOverride, eventQrLinks, isValidLink, loadQrPdfSettings, saveQrPdfSettings, selectedQrPages } from '../utils/eventQrLinks';

const PAGE = { width: 210, height: 297, margin: 15 };
const CARD_STYLE = {
  subtext: '',
  foreground: '#111827',
  background: '#ffffff',
  accent: '#2563eb',
  size: 'large',
  errorLevel: 'M',
  frame: true,
};

const fileName = (name) => `${String(name || 'event').replace(/[^\p{L}\p{N}]+/gu, '_').replace(/^_+|_+$/g, '') || 'event'}_QR_codes.pdf`;

/** One A4 page per link: event name + link name above the QR, the URL below. */
const EventQrPdfDialog = ({ open, onClose, eventId, eventName, purposes = [] }) => {
  const { showSuccess, showError } = useToast();
  const links = useMemo(() => eventQrLinks(eventId, purposes), [eventId, purposes]);
  const [settings, setSettings] = useState(() => loadQrPdfSettings(eventId));
  const [isGenerating, setIsGenerating] = useState(false);
  const [editingKey, setEditingKey] = useState(null);

  useEffect(() => {
    if (open) setSettings(loadQrPdfSettings(eventId));
  }, [open, eventId]);

  const pages = selectedQrPages(links, settings);
  const toggle = (key) => setSettings((prev) => ({
    ...prev,
    excluded: prev.excluded.includes(key) ? prev.excluded.filter((k) => k !== key) : [...prev.excluded, key],
  }));
  const updateOverride = (key, patch) => setSettings((prev) => ({
    ...prev,
    overrides: { ...prev.overrides, [key]: { ...(prev.overrides?.[key] || {}), ...patch } },
  }));
  const resetOverride = (key) => setSettings((prev) => {
    const { [key]: _removed, ...rest } = prev.overrides || {};
    return { ...prev, overrides: rest };
  });
  const updateCustom = (id, patch) => setSettings((prev) => ({
    ...prev,
    customLinks: prev.customLinks.map((link) => (link.id === id ? { ...link, ...patch } : link)),
  }));
  const addCustom = () => setSettings((prev) => ({
    ...prev,
    customLinks: [...prev.customLinks, { id: `custom_${Date.now()}`, label: '', url: '', included: true }],
  }));
  const removeCustom = (id) => setSettings((prev) => ({
    ...prev,
    customLinks: prev.customLinks.filter((link) => link.id !== id),
  }));

  const generate = async () => {
    if (!pages.length) return;
    setIsGenerating(true);
    try {
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const canvas = document.createElement('canvas');
      for (let i = 0; i < pages.length; i += 1) {
        const page = pages[i];
        await drawQrCard(canvas, {
          ...CARD_STYLE,
          content: page.url,
          heading: `${eventName || ''}\n${page.label}`.trim(),
          footer: settings.showUrl ? page.url : '',
        });
        const maxW = PAGE.width - PAGE.margin * 2;
        const maxH = PAGE.height - PAGE.margin * 2;
        const scale = Math.min(maxW / canvas.width, maxH / canvas.height);
        const w = canvas.width * scale;
        const h = canvas.height * scale;
        if (i > 0) pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', (PAGE.width - w) / 2, (PAGE.height - h) / 2, w, h);
      }
      pdf.save(fileName(eventName));
      saveQrPdfSettings(eventId, settings);
      showSuccess(`Downloaded ${pages.length} QR code${pages.length === 1 ? '' : 's'}`);
      onClose();
    } catch (err) {
      showError(err.message || 'Failed to create the QR PDF');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => !isGenerating && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Download QR codes (PDF)</DialogTitle>
          <DialogDescription>
            One page per link. Your selection and extra links are remembered for next time on this browser.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-5">
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-900">Event links</p>
              {links.map((link) => {
                const shown = applyOverride(link, settings.overrides);
                const edit = settings.overrides?.[link.key] || {};
                const isEditing = editingKey === link.key;
                return (
                  <div key={link.key} className="rounded-md border px-3 py-2 text-sm">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4"
                        checked={!settings.excluded.includes(link.key)}
                        onChange={() => toggle(link.key)}
                        aria-label={`Include ${shown.label}`}
                      />
                      <div className="min-w-0 flex-1">
                        <span className="block font-medium text-gray-900">
                          {shown.label}
                          {shown.edited && <span className="ml-2 text-xs font-normal text-amber-700">edited</span>}
                        </span>
                        <span className="block truncate text-xs text-gray-500">{shown.url}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingKey(isEditing ? null : link.key)}
                        aria-label={`Edit ${shown.label}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </div>
                    {isEditing && (
                      <div className="mt-2 space-y-2 pl-7">
                        <Input
                          placeholder={link.label}
                          value={edit.label || ''}
                          onChange={(e) => updateOverride(link.key, { label: e.target.value })}
                          aria-label="Heading"
                        />
                        <Input
                          placeholder={link.url}
                          value={edit.url || ''}
                          onChange={(e) => updateOverride(link.key, { url: e.target.value })}
                          className={edit.url && !isValidLink(edit.url) ? 'border-red-400' : ''}
                          aria-label="Link"
                        />
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Leave a box empty to keep the default.</span>
                          {shown.edited && (
                            <Button type="button" variant="ghost" size="sm" onClick={() => resetOverride(link.key)}>
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Reset
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-900">Extra links</p>
                <Button type="button" variant="outline" size="sm" onClick={addCustom}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add link
                </Button>
              </div>
              {settings.customLinks.length === 0 && (
                <p className="text-xs text-gray-500">Add any other link (feedback form, venue map…) with its own heading.</p>
              )}
              {settings.customLinks.map((link) => (
                <div key={link.id} className="flex items-start gap-2 rounded-md border px-3 py-2">
                  <input
                    type="checkbox"
                    className="mt-2.5 h-4 w-4"
                    checked={link.included !== false}
                    onChange={(e) => updateCustom(link.id, { included: e.target.checked })}
                    aria-label="Include this link"
                  />
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Input
                      placeholder="Heading, e.g. Feedback form"
                      value={link.label}
                      onChange={(e) => updateCustom(link.id, { label: e.target.value })}
                    />
                    <Input
                      placeholder="https://…"
                      value={link.url}
                      onChange={(e) => updateCustom(link.id, { url: e.target.value })}
                      className={link.url && !isValidLink(link.url) ? 'border-red-400' : ''}
                    />
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeCustom(link.id)} aria-label="Remove link">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={settings.showUrl}
                onChange={(e) => setSettings((prev) => ({ ...prev, showUrl: e.target.checked }))}
              />
              Print the link under each QR code
            </label>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isGenerating}>Cancel</Button>
          <Button onClick={generate} disabled={isGenerating || pages.length === 0}>
            <Download className="w-4 h-4 mr-2" />
            {isGenerating ? 'Creating PDF…' : `Download PDF (${pages.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EventQrPdfDialog;
